import styled, { css } from 'styled-components';
import theme from '../../theme';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100vw !important;
    max-width: 1920px !important;
    height: 100vh !important;

    .print-agreement {
        display: none;
    }
`;

const Header = styled.div<{
    $isPrintOut: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 88px;
    padding: 0 20px;

    ${props => props.$isPrintOut && css`
        padding: 32px 64px;
    `};

    img:first-of-type {
        max-height: 60px;
    }

    img:last-of-type {
        max-height: 60px;
        max-width: 200px;
    }

    @media only screen and (max-width: 600px) {
        ${props => !props.$isPrintOut && css`
            height: 58px;

            img:first-of-type {
                max-height: 45px;
            }

            img:last-of-type {
                max-height: 45px;
                max-width: 120px;
            }
        `};
    }
`;

const Content = styled.div<{
    $isMobile: boolean;
    $isPrintOut: boolean;
    $templateUuid: string;
    $templateImage: string;
    $textColor: string;
}>`
    height: calc(100vh - 88px) !important;
    color: ${props => props.$textColor};
    background-size: cover;
    background-repeat: repeat;
    background-position: left top;
    overflow-y: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-thumb {
        background: transparent;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    ${props => css`
        background-image: url(${props.$templateImage});
        padding: ${props.$isMobile ? '16px 32px' : '32px 64px'};
    `};

    ${props => !props.$isMobile && !props.$isPrintOut && css`
        > div {
            max-width: 50%;
        }
    `};

    ${props => props.$isPrintOut && css`
        color: black;
        background-image: none;
        padding: 32px 64px;
    `};

    h1 {
        font-size: 36px;
        font-weight: 800;
    }

    h2 {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 10px;
    }

    h4 {
        margin-top: 20px;
        font-size: 16px;
        font-weight: 700;

        span {
            font-size: 20px;
            font-weight: 700;
        }
    }

    h5 {
        font-size: 13px;
        font-weight: 400;
        margin-bottom: 20px;

        span {
            font-weight: 700;
        }
    }
`;

const VoucherContent = styled.div<{
    $isMobile: boolean;
    $isPrintOut: boolean;
    $isVouchersVisible: boolean;
    $textColor: string;
}>`
    display: flex;
    align-content: center;
    justify-content: center;
    margin: 20px 0 5px 0;
    color: black;
    background-color: white;
    font-size: 20px;
    font-weight: 400;
    text-align: center;
    border-radius: 8px;
    padding: 15px 20px;
    max-width: 434px;
    cursor: pointer;

    ${({ $textColor }) => $textColor === 'BLACK' && css`
        background-color: ${theme.secondaryColor};
    `};

    ${props => props.$isPrintOut && css`
        background-color: white;
        color: black;
        border-color: transparent;
        justify-content: start;
        padding: 0;
        margin-bottom: 20px;
    `};
   
    ul {
        list-style-type: none;
    }
`;

const ButtonsRow = styled.div<{
    $isMobile: boolean;
    $isPrintOut: boolean;
}>`
    display: flex;
    flex-direction: ${props => props.$isMobile ? 'column' : 'row'};
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    max-width: 474px;

    button {
        width: 100%;
    }

    ${props => props.$isPrintOut && css`
        display: none;
    `};
`;

const ExpiredContent = styled.div<{
    $isMobile: boolean;
}>`
    padding-top: calc(25% - 32px);

    ${props => props.$isMobile && css`
        padding-top: calc(50% - 16px);
    `};

    h1 {
        font-size: 36px;
        font-weight: 800;
    }

    h2 {
        font-size: 24px;
        font-weight: 500;
        margin-top: 20px;
    }
`;

const CustomInnerHTML = styled.div<{
    $textColor: string;
    $isPrintOut: boolean;
}>`
    font-size: 12px;
    font-weight: 400;
    background-color: transparent !important;
    color: ${props => props.$textColor} !important;
    
    ${props => props.$isPrintOut && css`
        color: black !important;
    `};

    h1, h2, h3, h4, h5, h6, p, strong, small, li, span, div, em, header, section, nav, aside, hr, ul, ol, li, a {
        font-size: 12px;
        font-weight: 400;
        background-color: transparent !important;
        color: ${props => props.$textColor} !important;

        ${props => props.$isPrintOut && css`
            color: black !important;
        `};
    }

    ul, ol {
        margin-left: 40px;
    }

    a:link {
        color: #0472B7;
    }

    a:visited {
        color: #0472B7;
    }

    a:hover {
        color: #0472B7;
    }

    a:active {
        color: #0472B7;
    }
`;

const Instructions = styled(CustomInnerHTML)`
    margin: 20px 0;
`;

export { Container, Header, Content, VoucherContent, ButtonsRow, ExpiredContent, Instructions, CustomInnerHTML };